import React, { forwardRef, ReactElement, useEffect, useMemo, useState } from "react"

import { HorizontalCalendar } from "../HorizontalCalendar/HorizontalCalendar"
import { filterInvalidAvailabilities } from "../utils/agenda"
import { DailyAvailabilitiesSelector } from "../DailyAvailabilitiesSelector/DailyAvailabilitiesSelector"
import { AddressAvailability } from "types/agenda.types"
import moment from "moment"
import {
  DateOnVacationContainer,
  DateOnVacationDescription,
  DateOnVacationTitle,
  NextVacationContainer,
  NextVacationText,
  NextVacationTitle,
  Wrapper,
} from "./AgendaSelector.styled"
import { KeeperVacation } from "types/keeper.types"

interface AgendaSelectorProps {
  agenda: AddressAvailability[]
  onAvailabilitiesUpdated: (availabilities?: AddressAvailability[]) => void
  vacation: KeeperVacation | null
}

function AgendaSelectorComponent({ agenda, vacation }: AgendaSelectorProps): ReactElement {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [liveAvailabilities, setLiveAvailabilities] = useState<AddressAvailability[]>([])

  useEffect(() => {
    setLiveAvailabilities(filterInvalidAvailabilities(agenda))
  }, [agenda])

  /**
   * When a date is selected, we get the availabilities for this day in the live availabilities
   */
  const liveAvailabilitiesForDate: AddressAvailability[] = useMemo(() => {
    return liveAvailabilities.filter(({ date }) => date === moment(selectedDate).format("YYYY-MM-DD"))
  }, [liveAvailabilities, selectedDate])

  const daysWithAtLeastOneAvailability = useMemo(() => {
    return [...new Set(liveAvailabilities.map(({ date }) => date))]
  }, [liveAvailabilities])

  const isDateOnVacation = useMemo(() => {
    return selectedDate && vacation && moment(selectedDate).isBetween(vacation.start, vacation.end, null, "[]")
  }, [selectedDate, vacation])

  return (
    <Wrapper>
      {vacation && (
        <NextVacationContainer>
          <NextVacationText>
            <NextVacationTitle>🏖️ Prochaines vacances</NextVacationTitle> : du{" "}
            {moment(vacation.start).format("DD/MM/YYYY")} au {moment(vacation.end).format("DD/MM/YYYY")}
          </NextVacationText>
        </NextVacationContainer>
      )}
      <HorizontalCalendar
        onDateSelected={setSelectedDate}
        daysWithAtLeastOneAvailability={daysWithAtLeastOneAvailability}
        startDate={moment().startOf("day").toDate()}
      />
      {isDateOnVacation && (
        <DateOnVacationContainer>
          <DateOnVacationTitle>En vacances 🏖️</DateOnVacationTitle>
          <DateOnVacationDescription>
            Non affiché sur les cartes du {moment(vacation.start).format("DD/MM/YYYY")} au{" "}
            {moment(vacation.end).format("DD/MM/YYYY")}
          </DateOnVacationDescription>
        </DateOnVacationContainer>
      )}

      {!isDateOnVacation && liveAvailabilitiesForDate && (
        <DailyAvailabilitiesSelector dayAvailabilities={liveAvailabilitiesForDate} />
      )}
    </Wrapper>
  )
}

export const AgendaSelector = forwardRef(AgendaSelectorComponent)
